import { defineStore } from 'pinia'
import { computed, type Ref, ref, watch } from 'vue'

import { useLoader } from '@/components/composables/loader.ts'
import { DataServiceFactory, EntraIDAuthService, SiteCapacityService } from '@/factories'
import type { IHeadcount, ISiteCapacity, ISiteHeadcount, IStoreEntity } from '@/interfaces'
import { SiteCapacity, SiteHeadcount } from '@/models'
import { EntraIDAuthConfig } from '@/services'
import { type IFullStore, useAuthStore, useBaseStore } from '@/stores'

export interface ISiteCapacityStore<T extends IStoreEntity, TI = T> extends IFullStore<T, TI> {
	// activeScenarios: Ref<IScenarioSubJob[]>
	// getScenarios(id: number): Promise<IScenarioSubJob[]>
	// getBaseline(id: number): Promise<IScenarioSubJob>
	// commit(id: string): Promise<IScenarioSubJob>
	activeHeadcount: Ref<ISiteHeadcount>
	ftePlan: Ref<IHeadcount[]>
	printBlank(): void
	getHeadcount(): Promise<ISiteHeadcount>
	saveHeadcount(record: SiteHeadcount): Promise<ISiteHeadcount>
}

const authService = DataServiceFactory.createAuthService('EntraIDAuth', EntraIDAuthConfig) as EntraIDAuthService
// const forecastService = DataServiceFactory.createService('Forecast', authService) as ForecastService
const siteCapacityService = DataServiceFactory.createService('SiteCapacity', authService) as SiteCapacityService

export const useSiteCapacityStore = defineStore<
	'site-capacity-store',
	ISiteCapacityStore<ISiteCapacity, ISiteCapacity>
>('site-capacity-store', () => {
	const {
		id,
		records,
		activeRecord,
		searchTerm,
		preFilters,
		currentPage,
		sortCriteria,
		storeActive,
		isDuplicating,
		activeRecordSavedHash,
		indexView,
		// indexFilter,
		// indexParams,
		viewList,
		// filterList,
		pageSize,

		elevatedPermissions,
		currentFormHash,
		currentFormDirty,
		preFilteredRecords,
		searchedRecords,
		searchRecordIndex,
		searchedRecordLength,
		currentPages,
		totalPages,
		recordCount,
		previousRecord,
		nextRecord,
		orderedRecords,
		pagedRecords,

		canRead,
		canEdit,
		canAdd,
		clearActive,
		clearSearch,
		calcHash,

		// Column filter methods
		columnFilters,
		activeColumnFilters,
		hasActiveColumnFilters,
		activeColumnFilterCount,
		setColumnFilter,
		clearColumnFilter,
		clearAllColumnFilters,
		getColumnFilter,
		hasColumnFilter
	} = useBaseStore<ISiteCapacity, 'SiteCapacity', ISiteCapacity>('SiteCapacity')

	const getAuthStore = computed(() => {
		return useAuthStore()
	})

	const indexFilter = ref('currentrecords')

	const loader = useLoader()

	const filterList = ref([
		{ value: 'allrecords', label: 'All Records' },
		{ value: 'currentrecords', label: 'Current Records' },
		{ value: '6weeks', label: 'Next 6 Weeks' },
		{ value: '12weeks', label: 'Next 12 Weeks' },
		{ value: 'custom', label: 'Custom Range' }
	])

	const indexParams = computed(() => {
		if (indexFilter.value === 'allrecords') {
			return {}
		} else if (indexFilter.value === 'currentrecords') {
			return { currentFlag: 'true' }
		} else {
			return {}
		}
	})

	// const activeScenarios = ref<IScenarioSubJob[]>([])

	const activeHeadcount = ref<ISiteHeadcount>({} as ISiteHeadcount)
	const ftePlan = ref<IHeadcount[]>([])

	async function getIndex(): Promise<ISiteCapacity[]> {
		return siteCapacityService
			.getIndex(indexParams.value)
			.then((data) => {
				records.value = data
				return records.value
			})
			.catch((err) => {
				console.error(err)
				records.value = []
				return records.value
			})
	}

	async function getHeadcount(): Promise<ISiteHeadcount> {
		return siteCapacityService
			.getHeadcount()
			.then((data) => {
				activeHeadcount.value = data
				updateFtePlan()
				return activeHeadcount.value
			})
			.catch((err) => {
				console.error(err)
				return {} as ISiteHeadcount
			})
	}

	async function saveHeadcount(record: ISiteHeadcount): Promise<ISiteHeadcount> {
		return siteCapacityService
			.updateHeadcount(record)
			.then((data) => {
				activeHeadcount.value = data
				updateFtePlan()
				return data
			})
			.catch((err) => {
				console.error(err)
				throw err
			})
	}

	async function get(id: string): Promise<ISiteCapacity> {
		if (id === '0') {
			return addNew()
		}
		return siteCapacityService
			.get(encodeURIComponent(id))
			.then((data) => {
				console.log(data)
				activeRecord.value = data
				activeRecordSavedHash.value = calcHash(data)
				return activeRecord.value
			})
			.catch((err) => {
				console.error(err)
				activeRecord.value = {} as ISiteCapacity
				return activeRecord.value
			})
	}

	async function printBlank() {
		console.log('printBlank')
	}

	// TODO figure out how we want to implement a different 'active record' from scenario vs. forecast
	function addNew(): ISiteCapacity {
		const record = new SiteCapacity({} as ISiteCapacity)
		record.id = 0
		record.subjobId = 0
		activeRecord.value = record
		activeRecordSavedHash.value = calcHash(record)
		return activeRecord.value
	}

	async function save(record: ISiteCapacity): Promise<ISiteCapacity> {
		console.log(record)
		return siteCapacityService
			.update(record)
			.then((data) => {
				activeRecord.value = data
				activeRecordSavedHash.value = calcHash(data)
				return activeRecord.value
			})
			.catch((err) => {
				console.error(err)
				throw err
			})
		// }
	}

	async function updateFtePlan() {
		const headCountObj = activeHeadcount.value
		ftePlan.value = [
			{ site: 'pdx', headcount: headCountObj.portlandHeadcount || 0 },
			{ site: 'mh', headcount: headCountObj.morganHillHeadcount || 0 },
			{ site: 'la', headcount: headCountObj.losAngelesHeadcount || 0 },
			{ site: 'chi', headcount: headCountObj.chicagoHeadcount || 0 },
			{ site: 'nc', headcount: headCountObj.northCarolinaHeadcount || 0 },
			{ site: 'har', headcount: headCountObj.hartfordHeadcount || 0 },
			{ site: 'tru', headcount: headCountObj.trumbullHeadcount || 0 }
		]
	}

	watch(
		() => indexParams.value,
		async () => {
			loader.show()
			await getIndex()
			loader.hide()
		}
	)

	if (!records.value.length) {
		getIndex()
	}

	if (!activeHeadcount.value.length) {
		getHeadcount()
	}

	return {
		id,
		records,
		activeRecord,
		searchTerm,
		preFilters,
		currentPage,
		sortCriteria,
		storeActive,
		isDuplicating,
		activeRecordSavedHash,
		indexView,
		indexFilter,
		indexParams,
		viewList,
		filterList,
		pageSize,
		activeHeadcount,
		ftePlan,

		elevatedPermissions,
		currentFormHash,
		currentFormDirty,
		preFilteredRecords,
		searchedRecords,
		searchRecordIndex,
		searchedRecordLength,
		currentPages,
		totalPages,
		recordCount,
		previousRecord,
		nextRecord,
		orderedRecords,
		pagedRecords,
		getAuthStore,

		printBlank,
		clearSearch,
		clearActive,
		get,
		getIndex,
		save,
		addNew,
		canRead,
		canEdit,
		canAdd,
		getHeadcount,
		saveHeadcount,
		calcHash,

		// Column filter methods
		columnFilters,
		activeColumnFilters,
		hasActiveColumnFilters,
		activeColumnFilterCount,
		setColumnFilter,
		clearColumnFilter,
		clearAllColumnFilters,
		getColumnFilter,
		hasColumnFilter
	}
})
